/* assets/global.js */

class PredictiveSearch {
    constructor() {
        this.container = document.getElementById('predictive-search');
        this.input = document.querySelector('.header__search-input');
        this.form = document.querySelector('.header__search-form');
        this.setupEventListeners();
    }

    setupEventListeners() {
        if (!this.input || !this.container) return;

        this.input.addEventListener('input', this.debounce((event) => {
            this.onChange(event);
        }, 300).bind(this));

        // Close on blur or outside click
        document.addEventListener('click', (event) => {
            if (!this.container.contains(event.target) && event.target !== this.input) {
                this.close();
            }
        });

        // Handle keyboard navigation
        this.input.addEventListener('keydown', this.onKeydown.bind(this));
    }

    onKeydown(event) {
        if (event.code === 'Enter' && this.container.style.display === 'block') {
            const selectedItem = this.container.querySelector('[aria-selected="true"]');
            if (selectedItem) {
                event.preventDefault();
                selectedItem.click();
            }
        }
    }

    onChange() {
        const searchTerm = this.input.value.trim();

        if (!searchTerm.length) {
            this.close();
            return;
        }

        this.getSearchResults(searchTerm);
    }

    async getSearchResults(searchTerm) {
        this.container.innerHTML = '<div class="predictive-search__loading-state">Caricamento...</div>';
        this.container.style.display = 'block';

        try {
            const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=5&section_id=predictive-search`);

            // Note: Shopify's suggest.json returns structured data. 
            // We'll use the API response to build the HTML manually here for speed.
            const data = await response.json();
            const products = data.resources.results.products;

            if (products && products.length > 0) {
                this.renderSearchResults(products);
            } else {
                this.container.innerHTML = '<div class="predictive-search__no-results">Nessun prodotto trovato per "' + searchTerm + '"</div>';
            }
        } catch (error) {
            console.error('Predictive Search Error:', error);
            this.close();
        }
    }

    renderSearchResults(products) {
        const isEnglish = window.location.pathname.startsWith('/en');
        const html = products.map(product => {
            let translatedTitle = product.title;
            return `
            <a href="${product.url}" class="predictive-search__item" role="option">
                <img src="${product.image ? product.image : ''}" class="predictive-search__image" alt="${translatedTitle}">
                <div class="predictive-search__item-content">
                    <span class="predictive-search__item-vendor">${product.vendor || ''}</span>
                    <h3 class="predictive-search__item-title">${translatedTitle}</h3>
                    <span class="predictive-search__item-price">${this.formatMoney(product.price)}</span>
                </div>
            </a>
        `}).join('');

        this.container.innerHTML = html;
        this.container.style.display = 'block';
    }

    formatMoney(price) {
        // Simple formatter
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);
    }

    debounce(fn, wait) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        };
    }

    close() {
        if (this.container) this.container.style.display = 'none';
    }
}

class HeaderComponent extends HTMLElement {
    constructor() {
        super();
        this.header = this;
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll(); // Init
        this.initSearch();
        this.initMobileMenu();
        this.predictiveSearch = new PredictiveSearch();
    }

    initMobileMenu() {
        const trigger = this.querySelector('.js-mobile-menu-trigger');
        const drawer = this.querySelector('.mobile-drawer');
        const closeButtons = this.querySelectorAll('.js-mobile-menu-close');

        if (trigger && drawer) {
            trigger.addEventListener('click', () => {
                drawer.classList.add('is-open');
                document.body.style.overflow = 'hidden';
            });

            closeButtons.forEach(btn => {
                btn.addEventListener('click', () => {
                    drawer.classList.remove('is-open');
                    document.body.style.overflow = '';
                });
            });

            // Close on ESC
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    drawer.classList.remove('is-open');
                    document.body.style.overflow = '';
                }
            });
        }
    }

    initSearch() {
        const searchTriggers = this.querySelectorAll('.js-search-trigger');
        const headerElement = this.querySelector('.header');
        const searchInput = this.querySelector('.header__search-input');

        if (searchTriggers.length > 0 && headerElement) {
            searchTriggers.forEach(trigger => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    const isOpen = headerElement.classList.contains('header--search-open');

                    if (isOpen) {
                        headerElement.classList.remove('header--search-open');
                    } else {
                        headerElement.classList.add('header--search-open');
                        // Focus input after transition
                        setTimeout(() => {
                            if (searchInput) searchInput.focus();
                        }, 100);
                    }
                });
            });

            // Close on Escape key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && headerElement.classList.contains('header--search-open')) {
                    headerElement.classList.remove('header--search-open');
                }
            });

            // Close when clicking outside (optional, but good UX)
            document.addEventListener('click', (e) => {
                if (headerElement.classList.contains('header--search-open') &&
                    !this.contains(e.target)) {
                    headerElement.classList.remove('header--search-open');
                }
            });
        }
    }

    onScroll() {
        if (!this.ticking) {
            window.requestAnimationFrame(() => {
                const scrollTop = window.scrollY;
                const header = this.querySelector('.header');
                if (header) {
                    if (scrollTop > 50) {
                        header.classList.add('header--scrolled');
                    } else {
                        header.classList.remove('header--scrolled');
                    }
                }
                this.ticking = false;
            });
            this.ticking = true;
        }
    }
}

class ProductInfo extends HTMLElement {
    constructor() {
        super();
        this.initAccordions();
        this.initVariantPicker();
        this.initQuantitySelector();
        this.initGallery();
        this.initStickyCart();

        // Load variants
        const script = this.querySelector('[data-product-variants]');
        this.variants = script ? JSON.parse(script.textContent) : [];

        // Run initial availability check to hide unavailable sizes for the pre-selected color
        if (this.variants.length > 0) {
            this.onVariantChange();
        }
    }

    initStickyCart() {
        this.stickyCart = document.querySelector('.sticky-add-to-cart');
        this.mainAddToCart = this.querySelector('.product-form__submit');

        if (this.stickyCart && this.mainAddToCart) {
            const observerOptions = {
                root: null, // use viewport
                threshold: 0, // trigger as soon as even one pixel is visible
                rootMargin: '0px'
            };

            const observerCallback = (entries) => {
                entries.forEach(entry => {
                    // Show sticky bar ONLY when main button is NOT in view (scrolled past)
                    // We check if it's NOT intersecting AND its top position is negative (above viewport)
                    if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
                        this.stickyCart.classList.add('is-visible');
                    } else {
                        this.stickyCart.classList.remove('is-visible');
                    }
                });
            };

            const observer = new IntersectionObserver(observerCallback, observerOptions);
            observer.observe(this.mainAddToCart);
        }
    }

    initGallery() {
        this.gallerySlider = this.querySelector('[data-gallery-slider]');
        this.dots = this.querySelectorAll('.product-gallery__dot');

        if (this.gallerySlider && this.dots.length > 0) {
            const observerOptions = {
                root: this.gallerySlider,
                threshold: 0.5
            };

            const observerCallback = (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const index = entry.target.dataset.index;
                        this.updateDots(parseInt(index));
                    }
                });
            };

            const observer = new IntersectionObserver(observerCallback, observerOptions);
            this.querySelectorAll('.product-gallery__slide').forEach(slide => observer.observe(slide));

            // Handle dot clicks
            this.dots.forEach(dot => {
                dot.addEventListener('click', () => {
                    const index = parseInt(dot.dataset.index);
                    const slides = this.querySelectorAll('.product-gallery__slide');
                    if (slides[index]) {
                        slides[index].scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                            inline: 'start'
                        });
                    }
                });
            });
        }
    }

    updateDots(index) {
        this.dots.forEach((dot, i) => {
            if (i === index) {
                dot.classList.add('is-active');
            } else {
                dot.classList.remove('is-active');
            }
        });
    }


    initAccordions() {
        this.querySelectorAll('[data-accordion-trigger]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const content = btn.nextElementSibling;
                const isOpen = content.classList.contains('is-open');

                content.classList.toggle('is-open');
                const svg = btn.querySelector('svg');
                if (svg) {
                    svg.style.transform = isOpen ? 'rotate(0deg)' : 'rotate(180deg)';
                }
            });
        });
    }

    initQuantitySelector() {
        const input = this.querySelector('.quantity-input');
        const btns = this.querySelectorAll('.quantity-button');

        if (!input) return;

        btns.forEach(btn => {
            btn.addEventListener('click', () => {
                const isPlus = btn.getAttribute('name') === 'plus';
                const currentVal = parseInt(input.value);
                if (isPlus) {
                    input.value = currentVal + 1;
                } else if (currentVal > 1) {
                    input.value = currentVal - 1;
                }
            });
        });
    }

    initVariantPicker() {
        // Grid Options
        this.querySelectorAll('.variant-picker__option').forEach((opt) => {
            opt.addEventListener('click', () => {
                if (opt.classList.contains('is-disabled')) return;

                const group = opt.closest('.variant-picker__group');
                if (group) {
                    group.querySelectorAll('.variant-picker__option').forEach((el) => el.classList.remove('is-selected'));
                    opt.classList.add('is-selected');

                    // Sync Select if exists
                    const select = group.querySelector('.variant-picker__select');
                    if (select) {
                        select.value = opt.getAttribute('data-value');
                    }

                    this.onVariantChange();
                }
            });
        });

        // Select Options
        this.querySelectorAll('.variant-picker__select').forEach((select) => {
            select.addEventListener('change', () => {
                const group = select.closest('.variant-picker__group');
                const value = select.value;

                if (group) {
                    // Sync Grid if exists
                    const options = group.querySelectorAll('.variant-picker__option');
                    options.forEach(opt => {
                        if (opt.getAttribute('data-value') === value) {
                            opt.classList.add('is-selected');
                        } else {
                            opt.classList.remove('is-selected');
                        }
                    });

                    this.onVariantChange();
                }
            });
        });
    }

    onVariantChange() {
        const selectedOptions = Array.from(this.querySelectorAll('.variant-picker__group')).map((group, index) => {
            const selected = group.querySelector('.variant-picker__option.is-selected');
            let val = null;
            // Also check select if swatch isn't found
            if (!selected) {
                const select = group.querySelector('.variant-picker__select');
                val = select ? select.value : null;
            } else {
                val = selected.getAttribute('data-value');
            }
            return val;
        });

        // Update options availability and auto-correct selectedOptions if needed
        this.updateOptionsAvailability(selectedOptions);

        // Now update labels and DOM with the potentially corrected selectedOptions
        Array.from(this.querySelectorAll('.variant-picker__group')).forEach((group, index) => {
            let displayVal = selectedOptions[index];
            if (!displayVal) return;
            
            if (window.shopDetails && window.shopDetails.locale === 'en') {
                const translations = {
                    'Blu': 'Blue', 'Verde': 'Green', 'Nero': 'Black', 'Bianco': 'White',
                    'Rosso': 'Red', 'Grigio': 'Grey', 'Marrone': 'Brown', 'Giallo': 'Yellow',
                    'Arancione': 'Orange', 'Rosa': 'Pink', 'Azzurro': 'Light Blue',
                    'anni': 'years', 'Anni': 'Years'
                };
                Object.keys(translations).forEach(key => {
                    displayVal = displayVal.replace(key, translations[key]);
                });
            }

            const labelSpan = group.querySelector(`.js-selected-value`) || group.querySelector(`.js-selected-value-pattern-${index}`);
            if (labelSpan) {
                labelSpan.textContent = ': ' + displayVal;
            }

            // Sync select value
            const select = group.querySelector('.variant-picker__select');
            if (select && select.value !== selectedOptions[index]) {
                select.value = selectedOptions[index];
            }

            // Sync grid selection
            const options = group.querySelectorAll('.variant-picker__option');
            if (options.length > 0) {
                options.forEach(opt => {
                    if (opt.getAttribute('data-value') === selectedOptions[index]) {
                        opt.classList.add('is-selected');
                    } else {
                        opt.classList.remove('is-selected');
                    }
                });
            }
        });

        const currentVariant = this.variants.find(variant => {
            return variant.options.every((option, index) => {
                return option === selectedOptions[index];
            });
        });

        if (currentVariant) {
            this.updateProductInfo(currentVariant);
            this.filterGallery();
        } else {
            // Fallback if no variant exists
            const btn = this.querySelector('.product-form__submit');
            if (btn) {
                btn.setAttribute('disabled', 'true');
                const span = btn.querySelector('span');
                if (span) span.textContent = window.variantStrings?.unavailable || 'Non disponibile';
            }
            const priceEl = this.querySelector('[data-main-price]');
            if (priceEl) priceEl.textContent = '';
        }
    }

    filterGallery() {
        if (!this.gallerySlider) return;

        const selectedColorOption = this.querySelector('.variant-picker__option--swatch.is-selected');
        if (!selectedColorOption) return;

        const selectedColor = selectedColorOption.getAttribute('data-value');
        if (!selectedColor) return;

        const items = this.gallerySlider.querySelectorAll('.product-gallery__item');
        let hasColoredItems = false;
        let hasVisibleItems = false;

        // First check if any items have data-color at all
        items.forEach(item => {
            if (item.hasAttribute('data-color')) hasColoredItems = true;
        });

        // If no items have data-color, don't filter (product has no color mapping)
        if (!hasColoredItems) return;

        items.forEach(item => {
            const itemColor = item.getAttribute('data-color');
            if (!itemColor) {
                // Items without data-color are hidden when filtering is active
                item.style.display = 'none';
            } else if (itemColor.toLowerCase() === selectedColor.toLowerCase()) {
                item.style.display = '';
                hasVisibleItems = true;
            } else {
                item.style.display = 'none';
            }
        });

        // Fallback: if no items matched, show all
        if (!hasVisibleItems) {
            items.forEach(item => {
                item.style.display = '';
            });
        }

        // Scroll gallery back to top/start
        this.gallerySlider.scrollTop = 0;
        this.gallerySlider.scrollLeft = 0;
    }

    updateOptionsAvailability(currentSelectedOptions) {
        const optionGroups = Array.from(this.querySelectorAll('.variant-picker__group'));
        optionGroups.forEach((group, index) => {
            const options = group.querySelectorAll('.variant-picker__option');
            const selectOptions = group.querySelectorAll('select option');
            let hasValidOption = false;

            options.forEach(opt => {
                const value = opt.getAttribute('data-value');
                
                const matchingVariant = this.variants.find(variant => {
                    if (variant.options[index] !== value) return false;
                    // Match previous option values (cascading)
                    for (let i = 0; i < index; i++) {
                        if (variant.options[i] !== currentSelectedOptions[i]) return false;
                    }
                    return variant.available;
                });

                if (matchingVariant) {
                    opt.classList.remove('is-disabled');
                    if (value === currentSelectedOptions[index]) hasValidOption = true;
                } else {
                    // Check if it exists at all even if not available
                    const exists = this.variants.find(variant => {
                        if (variant.options[index] !== value) return false;
                        for (let i = 0; i < index; i++) {
                            if (variant.options[i] !== currentSelectedOptions[i]) return false;
                        }
                        return true;
                    });
                    
                    if (exists) {
                        opt.classList.add('is-disabled');
                        if (value === currentSelectedOptions[index]) hasValidOption = true; // Still a valid combination, just sold out
                    } else {
                        opt.classList.add('is-disabled');
                        // It does not exist, so it's not a valid option
                    }
                }
            });

            selectOptions.forEach(opt => {
                const value = opt.value;
                const baseLabel = opt.getAttribute('data-base-label') || opt.textContent;

                const matchingVariant = this.variants.find(variant => {
                    if (variant.options[index] !== value) return false;
                    for (let i = 0; i < index; i++) {
                        if (variant.options[i] !== currentSelectedOptions[i]) return false;
                    }
                    return true;
                });

                if (matchingVariant) {
                    opt.style.display = '';
                    opt.disabled = false;
                    if (value === currentSelectedOptions[index]) hasValidOption = true;
                    
                    const isOnSale = matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price;
                    if (isOnSale) {
                        const price = this.formatMoney(matchingVariant.price);
                        const saleLabel = window.variantStrings?.onSale || 'SALE';
                        opt.textContent = `${baseLabel} - ${saleLabel} (${price})`;
                        opt.style.color = '#d32f2f';
                        opt.style.fontWeight = 'bold';
                    } else {
                        opt.textContent = baseLabel;
                        opt.style.color = '';
                        opt.style.fontWeight = '';
                    }
                } else {
                    opt.style.display = 'none';
                    opt.disabled = true;
                }
            });

            // Auto-correct if current selection is invalid (doesn't exist)
            if (!hasValidOption && currentSelectedOptions[index]) {
                let firstValid = null;
                if (options.length > 0) {
                    const validOpt = Array.from(options).find(opt => !opt.classList.contains('is-disabled'));
                    // If all are disabled (sold out), just find one that exists
                    const existingOpt = Array.from(options).find(opt => {
                        const val = opt.getAttribute('data-value');
                        return this.variants.some(v => v.options[index] === val && Array.from({length: index}).every((_, i) => v.options[i] === currentSelectedOptions[i]));
                    });
                    if (validOpt) firstValid = validOpt.getAttribute('data-value');
                    else if (existingOpt) firstValid = existingOpt.getAttribute('data-value');
                } else if (selectOptions.length > 0) {
                    const validOpt = Array.from(selectOptions).find(opt => !opt.disabled);
                    if (validOpt) firstValid = validOpt.value;
                }
                
                if (firstValid) {
                    currentSelectedOptions[index] = firstValid;
                }
            }
        });
    }

    formatMoney(cents) {
        const locale = window.shopDetails?.locale || 'it-IT';
        const currency = window.shopDetails?.currency || 'EUR';
        return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100);
    }

    updateProductInfo(variant) {
        // Update hidden input
        const input = this.querySelector('input[name="id"]');
        if (input) input.value = variant.id;

        // Update Price
        const priceEl = this.querySelector('[data-main-price]');
        if (priceEl) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                priceEl.innerHTML = `
                    <span style="color: #D32F2F; font-weight: bold;">${this.formatMoney(variant.price)}</span>
                    <span style="text-decoration: line-through; color: #999; margin-left: 8px; font-size: 0.9em;">${this.formatMoney(variant.compare_at_price)}</span>
                `;
            } else {
                priceEl.innerHTML = this.formatMoney(variant.price);
            }
        }

        // Update SKU
        const skuEl = this.querySelector('[data-product-sku]');
        if (skuEl) {
            skuEl.textContent = variant.sku ? `SKU: ${variant.sku}` : '';
        }

        // Update Button
        const btn = this.querySelector('.product-form__submit');
        if (btn) {
            const span = btn.querySelector('span');
            if (variant.available) {
                btn.removeAttribute('disabled');
                span.textContent = window.variantStrings.addToBag;
            } else {
                btn.setAttribute('disabled', 'true');
                span.textContent = window.variantStrings.soldOut;
            }
        }

        // Update Low Stock Warning
        const stockWarning = this.querySelector('.js-low-stock-warning');
        const stockText = this.querySelector('.js-low-stock-text');

        if (stockWarning && stockText) {
            const isEnglish = window.location.pathname.startsWith('/en');
            // Check if variant has inventory tracking and policy prevents overselling
            if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
                if (variant.inventory_quantity > 0 && variant.inventory_quantity <= 3) {
                    stockText.textContent = isEnglish ? `Hurry! Only ${variant.inventory_quantity} left in stock.` : `Affrettati! Solo ${variant.inventory_quantity} pezzi rimasti.`;
                    stockWarning.style.display = 'flex';
                } else {
                    stockWarning.style.display = 'none';
                }
            } else {
                stockWarning.style.display = 'none';
            }
        }

        // Update Back In Stock Trigger
        const bisWrapper = this.querySelector('.js-bis-trigger-wrapper');
        if (bisWrapper) {
            bisWrapper.style.display = variant.available ? 'none' : 'flex';
        }

        // Update BIS Variant Hidden Input
        const bisVariantInput = document.getElementById('BisVariantInput');
        if (bisVariantInput) {
            bisVariantInput.value = variant.title;
        }

        // Update URL
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        // Update Sticky Bar
        if (this.stickyCart) {
            const stickyPrice = this.stickyCart.querySelector('[data-sticky-price]');
            const stickyBtn = this.stickyCart.querySelector('.js-sticky-add');

            if (stickyPrice) {
                const cents = variant.price;
                const locale = window.shopDetails?.locale || 'it-IT';
                const currency = window.shopDetails?.currency || 'EUR';
                const fmt = new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100);
                stickyPrice.textContent = fmt;
            }

            if (stickyBtn) {
                if (variant.available) {
                    stickyBtn.removeAttribute('disabled');
                    stickyBtn.textContent = window.variantStrings.addToBag;
                } else {
                    stickyBtn.setAttribute('disabled', 'true');
                    stickyBtn.textContent = window.variantStrings.soldOut;
                }
            }
        }
    }
}

customElements.define('product-info', ProductInfo);
customElements.define('header-component', HeaderComponent);

/**
 * Back In Stock Modal Controller
 */
class BackInStock {
    constructor() {
        this.modal = document.getElementById('BackInStockModal');
        if (!this.modal) return;

        this.openButtons = document.querySelectorAll('.js-bis-open');
        this.closeButtons = this.modal.querySelectorAll('.js-bis-close');
        this.form = document.getElementById('BackInStockForm');

        this.init();
    }

    init() {
        this.openButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                this.open();
            });
        });

        this.closeButtons.forEach(btn => {
            btn.addEventListener('click', () => this.close());
        });

        if (this.form) {
            this.form.addEventListener('submit', (e) => this.handleSubmit(e));
        }

        // Close on escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
                this.close();
            }
        });
    }

    open() {
        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        // Focus first input
        const emailInput = this.modal.querySelector('#BisEmail');
        if (emailInput) setTimeout(() => emailInput.focus(), 300);
    }

    close() {
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
    }

    async handleSubmit(e) {
        e.preventDefault();
        const form = e.target;
        const submitBtn = form.querySelector('.bis-form__submit');
        const originalText = submitBtn.textContent;
        const isEnglish = window.location.pathname.startsWith('/en');

        submitBtn.disabled = true;
        submitBtn.textContent = isEnglish ? 'Sending...' : 'Invio...';

        try {
            const formData = new FormData(form);
            const response = await fetch('/contact', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const successMsg = isEnglish
                    ? 'Thank you! We will notify you as soon as it is back in stock.'
                    : 'Grazie! Ti avviseremo non appena sarà di nuovo disponibile.';
                form.innerHTML = `
          <div class="bis-form__success">
            <p>${successMsg}</p>
          </div>
        `;
            } else {
                throw new Error('Errore durante l\'invio');
            }
        } catch (error) {
            console.error('BIS Form Error:', error);
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
            const errorMsg = isEnglish
                ? 'An error occurred. Please try again later.'
                : 'Si è verificato un errore. Riprova più tardi.';
            alert(errorMsg);
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    window.BackInStockController = new BackInStock();
});


class QuickView {
    constructor() {
        this.modal = document.getElementById('QuickViewModal');
        this.overlay = document.getElementById('QuickViewOverlay');
        this.content = document.getElementById('QuickViewContent');
        this.closeBtn = this.modal?.querySelector('.quick-view-modal__close');
        this.init();
    }

    init() {
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest('.js-quick-view-trigger');
            if (trigger) {
                e.preventDefault();
                const handle = trigger.dataset.productHandle;
                if (handle) this.open(handle);
            }
        });

        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', () => this.close());
        }

        if (this.overlay) {
            this.overlay.addEventListener('click', () => this.close());
        }

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') this.close();
        });
    }

    async open(handle) {
        if (!this.modal || !this.content) return;

        // Show modal and loading
        this.modal.classList.add('is-open');
        this.overlay.classList.add('is-visible');
        document.body.style.overflow = 'hidden';
        this.content.innerHTML = '<div class="quick-view-loading"><span>Loading...</span></div>';

        try {
            const root = window.routes?.root || '';
            const fetchUrl = `${root === '/' ? '' : root}/products/${handle}?section_id=quick-view`;
            const response = await fetch(fetchUrl);
            const html = await response.text();
            this.content.innerHTML = html;
            this.initQuickViewContent();
        } catch (error) {
            console.error('Error fetching quick view:', error);
            this.content.innerHTML = '<div class="quick-view-error">Error loading product.</div>';
        }
    }

    close() {
        if (!this.modal) return;
        this.modal.classList.remove('is-open');
        this.overlay.classList.remove('is-visible');
        document.body.style.overflow = '';
    }

    cleanVariantName(name) {
        if (!name) return name;
        return name
            .replace(/ YEARS OLD/gi, ' YEARS')
            .replace(/ Years Old/gi, ' Years')
            .replace(/ years old/gi, ' years');
    }

    initQuickViewContent() {
        const variantOptions = this.modal.querySelectorAll('.variant-picker__option');
        const variantSelects = this.modal.querySelectorAll('.variant-picker__select');
        const customSelects = this.modal.querySelectorAll('.qv-custom-select');
        const variantsScript = this.modal.querySelector('[data-qv-variants]');
        this.qvVariants = variantsScript ? JSON.parse(variantsScript.textContent) : [];

        // Native select clicks
        variantSelects.forEach(select => {
            select.addEventListener('change', () => {
                const group = select.closest('.variant-picker__group');
                const value = select.value;
                if (group) {
                    // Sync swatches
                    group.querySelectorAll('.variant-picker__option').forEach(opt => {
                        opt.classList.toggle('is-selected', opt.getAttribute('data-value') === value);
                    });
                }
                this.onQvVariantChange();
            });
        });

        // Div swatch clicks (desktop)
        variantOptions.forEach(opt => {
            opt.addEventListener('click', () => {
                const group = opt.closest('.variant-picker__group');
                group.querySelectorAll('.variant-picker__option').forEach(el => el.classList.remove('is-selected'));
                opt.classList.add('is-selected');

                // Update selected value label if it exists
                const labelValue = group.querySelector('.variant-picker__label-value');
                if (labelValue) {
                    labelValue.textContent = opt.getAttribute('data-value');
                }

                // Sync custom dropdown label
                const customSelect = group.querySelector('.qv-custom-select');
                if (customSelect) {
                    const label = customSelect.querySelector('.qv-custom-select__value');
                    if (label) label.textContent = this.cleanVariantName(opt.getAttribute('data-value'));
                    // Sync custom options
                    customSelect.querySelectorAll('.qv-custom-select__option').forEach(li => {
                        li.classList.toggle('is-selected', li.getAttribute('data-value') === opt.getAttribute('data-value'));
                    });
                }

                this.onQvVariantChange();
            });
        });

        // Custom dropdown logic (mobile)
        customSelects.forEach(dropdown => {
            const trigger = dropdown.querySelector('.qv-custom-select__trigger');
            const options = dropdown.querySelectorAll('.qv-custom-select__option');

            // Toggle open/close
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                // Close other dropdowns
                this.modal.querySelectorAll('.qv-custom-select.is-open').forEach(d => {
                    if (d !== dropdown) d.classList.remove('is-open');
                    d.querySelector('.qv-custom-select__trigger').setAttribute('aria-expanded', 'false');
                });
                const isOpen = dropdown.classList.toggle('is-open');
                trigger.setAttribute('aria-expanded', isOpen);
            });

            // Option selection
            options.forEach(opt => {
                opt.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (opt.classList.contains('is-disabled')) return;

                    const value = opt.getAttribute('data-value');
                    const group = dropdown.closest('.variant-picker__group');

                    // Update custom dropdown UI
                    dropdown.querySelectorAll('.qv-custom-select__option').forEach(li => li.classList.remove('is-selected'));
                    opt.classList.add('is-selected');
                    trigger.querySelector('.qv-custom-select__value').textContent = this.cleanVariantName(value);
                    dropdown.classList.remove('is-open');
                    trigger.setAttribute('aria-expanded', 'false');

                    // Sync div swatches
                    if (group) {
                        group.querySelectorAll('.variant-picker__option').forEach(el => {
                            el.classList.toggle('is-selected', el.getAttribute('data-value') === value);
                        });
                    }

                    this.onQvVariantChange();
                });
            });
        });

        // Close dropdown on outside click
        document.addEventListener('click', () => {
            this.modal.querySelectorAll('.qv-custom-select.is-open').forEach(d => {
                d.classList.remove('is-open');
                d.querySelector('.qv-custom-select__trigger').setAttribute('aria-expanded', 'false');
            });
        });
    }

    onQvVariantChange() {
        const groups = this.modal.querySelectorAll('.variant-picker__group');
        const selectedOptions = Array.from(groups).map(group => {
            // Priority 1: Native Select
            const select = group.querySelector('.variant-picker__select');
            if (select && !select.closest('.js-hidden')) return select.value;

            // Priority 2: Swatch
            const selected = group.querySelector('.variant-picker__option.is-selected');
            if (selected) return selected.getAttribute('data-value');

            // Fallback: custom dropdown
            const customOpt = group.querySelector('.qv-custom-select__option.is-selected');
            return customOpt ? customOpt.getAttribute('data-value') : null;
        });

        const variant = this.qvVariants.find(v =>
            v.options.every((opt, i) => opt === selectedOptions[i])
        );

        this.updateQvOptionsAvailability(selectedOptions);

        if (variant) {
            const input = this.modal.querySelector('input[name="id"]');
            if (input) input.value = variant.id;

            if (priceEl) {
                const locale = window.shopDetails?.locale || 'it-IT';
                const currency = window.shopDetails?.currency || 'EUR';
                const fmt = new Intl.NumberFormat(locale, { style: 'currency', currency: currency });

                if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                    priceEl.innerHTML = `
                        <span style="color: #D32F2F; font-weight: bold;">${fmt.format(variant.price / 100)}</span>
                        <span style="text-decoration: line-through; color: #999; margin-left: 8px; font-size: 0.9em;">${fmt.format(variant.compare_at_price / 100)}</span>
                    `;
                } else {
                    priceEl.innerHTML = fmt.format(variant.price / 100);
                }
            }

            const btn = this.modal.querySelector('.quick-view-product__submit');
            if (btn) {
                const span = btn.querySelector('span');
                if (variant.available) {
                    btn.removeAttribute('disabled');
                    if (span) span.textContent = window.variantStrings.addToBag;
                } else {
                    btn.setAttribute('disabled', 'true');
                    if (span) span.textContent = window.variantStrings.soldOut;
                }
            }
        }
    }

    updateQvOptionsAvailability(currentSelectedOptions) {
        const optionGroups = Array.from(this.modal.querySelectorAll('.variant-picker__group'));
        optionGroups.forEach((group, index) => {
            const options = group.querySelectorAll('.variant-picker__option');
            const selectOptions = group.querySelectorAll('select option');
            const customOptions = group.querySelectorAll('.qv-custom-select__option');

            options.forEach(opt => {
                const value = opt.getAttribute('data-value');
                
                const matchingVariant = this.qvVariants.find(variant => {
                    // Match current option value
                    if (variant.options[index] !== value) return false;
                    
                    // Match previous option values (cascading)
                    for (let i = 0; i < index; i++) {
                        if (variant.options[i] !== currentSelectedOptions[i]) return false;
                    }
                    
                    return variant.available;
                });

                if (matchingVariant) {
                    opt.classList.remove('is-disabled');
                } else {
                    opt.classList.add('is-disabled');
                }
            });

            selectOptions.forEach(opt => {
                const value = opt.value;
                
                const matchingVariant = this.qvVariants.find(variant => {
                    if (variant.options[index] !== value) return false;
                    for (let i = 0; i < index; i++) {
                        if (variant.options[i] !== currentSelectedOptions[i]) return false;
                    }
                    return variant.available;
                });

                if (matchingVariant) {
                    opt.style.display = '';
                    opt.disabled = false;
                } else {
                    opt.style.display = 'none';
                    opt.disabled = true;
                }
            });

            customOptions.forEach(opt => {
                const value = opt.getAttribute('data-value');
                
                const matchingVariant = this.qvVariants.find(variant => {
                    if (variant.options[index] !== value) return false;
                    for (let i = 0; i < index; i++) {
                        if (variant.options[i] !== currentSelectedOptions[i]) return false;
                    }
                    return variant.available;
                });

                if (matchingVariant) {
                    opt.classList.remove('is-disabled');
                } else {
                    opt.classList.add('is-disabled');
                }
            });
        });
    }
}

// Global instance
window.quickView = new QuickView();

class QuickAdd extends HTMLElement {
    constructor() {
        super();
        this.init();
    }

    init() {
        this.addEventListener('click', this.onClick.bind(this));
    }

    async onClick(e) {
        e.preventDefault();
        const trigger = e.target.closest('[data-product-id]');
        if (!trigger) return;

        const variantId = trigger.dataset.productId;
        if (!variantId) return;

        trigger.setAttribute('disabled', true);
        trigger.classList.add('loading');

        // Add loading spinner if not present
        const originalText = trigger.innerHTML;
        trigger.innerHTML = '<span>...</span>';

        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: variantId,
                    quantity: 1
                })
            });

            const data = await response.json();

            if (data.status) {
                // Error handling (e.g. sold out)
                console.error(data.description);
                trigger.innerHTML = 'Error';
            } else {
                // Success
                document.dispatchEvent(new CustomEvent('cart:update'));

                // Optional: Open cart drawer if not open
                const cartDrawer = document.getElementById('CartDrawer');
                if (cartDrawer && !cartDrawer.classList.contains('is-open') && window.cartDrawer) {
                    window.cartDrawer.open();
                }

                // Reset button
                trigger.innerHTML = 'Added';
                setTimeout(() => {
                    trigger.innerHTML = originalText;
                }, 2000);
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            trigger.innerHTML = 'Error';
        } finally {
            trigger.removeAttribute('disabled');
            trigger.classList.remove('loading');
        }
    }
}
customElements.define('quick-add', QuickAdd);

class WishlistManager {
    constructor() {
        this.wishlistKey = 'npk_wishlist';
        this.items = this.getWishlist();
        this.init();
    }

    getWishlist() {
        const stored = localStorage.getItem(this.wishlistKey);
        return stored ? JSON.parse(stored) : [];
    }

    saveWishlist() {
        localStorage.setItem(this.wishlistKey, JSON.stringify(this.items));
        this.updateHeaderCount();
        document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: this.items } }));
    }

    clearAll() {
        this.items = [];
        this.saveWishlist();
        // Update all UIs
        document.querySelectorAll('.js-wishlist-toggle').forEach(btn => btn.classList.remove('is-active'));
    }

    share() {
        const url = new URL(window.location.href);
        const root = window.routes?.root || '';
        url.pathname = `${root === '/' ? '' : root}/pages/wishlist`;
        url.searchParams.set('handles', this.items.join(','));

        if (navigator.share) {
            navigator.share({
                title: 'La mia Wishlist Nupkeet',
                url: url.toString()
            }).catch(console.error);
        } else {
            // Fallback: Copy to clipboard
            navigator.clipboard.writeText(url.toString()).then(() => {
                alert('Link della wishlist copiato negli appunti!');
            });
        }
    }

    toggleItem(handle) {
        const index = this.items.indexOf(handle);
        if (index > -1) {
            this.items.splice(index, 1);
        } else {
            this.items.push(handle);
        }
        this.saveWishlist();
        this.updateUI(handle);
    }

    updateUI(handle) {
        const buttons = document.querySelectorAll(`.js-wishlist-toggle[data-product-handle="${handle}"]`);
        const isActive = this.items.includes(handle);
        buttons.forEach(btn => {
            if (isActive) {
                btn.classList.add('is-active');
            } else {
                btn.classList.remove('is-active');
            }
        });
    }

    updateHeaderCount() {
        const countElements = document.querySelectorAll('.js-wishlist-count');
        const count = this.items.length;
        countElements.forEach(el => {
            el.textContent = count;
            if (count > 0) {
                el.classList.remove('hidden');
            } else {
                el.classList.add('hidden');
            }
        });
    }

    init() {
        this.updateHeaderCount();

        // Use event delegation for wishlist toggles
        document.addEventListener('click', (e) => {
            const toggleBtn = e.target.closest('.js-wishlist-toggle');
            if (toggleBtn) {
                e.preventDefault();
                e.stopPropagation(); // Prevent card navigation
                const handle = toggleBtn.dataset.productHandle;
                if (handle) this.toggleItem(handle);
            }

            const clearBtn = e.target.closest('.js-wishlist-clear');
            if (clearBtn) {
                e.preventDefault();
                this.clearAll();
            }

            const shareBtn = e.target.closest('.js-wishlist-share');
            if (shareBtn) {
                e.preventDefault();
                this.share();
            }
        });

        // Initialize UI for all cards on load
        this.items.forEach(handle => this.updateUI(handle));

        // Listen for dynamic updates (e.g. after carousel load or AJAX)
        document.addEventListener('shopify:section:load', () => {
            this.items.forEach(handle => this.updateUI(handle));
        });
    }
}

class ShareManager {
    constructor() {
        this.init();
    }

    init() {
        document.addEventListener('click', (e) => {
            const shareBtn = e.target.closest('.js-share-btn');
            if (shareBtn) {
                e.preventDefault();
                this.handleShare(shareBtn);
            }
        });
    }

    async handleShare(btn) {
        const url = btn.dataset.shareUrl || window.location.href;
        const title = btn.dataset.shareTitle || document.title;
        const tooltip = btn.querySelector('.share-tooltip');

        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    url: url
                });
            } catch (err) {
                console.log('Share failed or cancelled:', err);
                // Fallback to clipboard if share was cancelled or failed for non-user reasons
                if (err.name !== 'AbortError') {
                    this.copyToClipboard(url, tooltip);
                }
            }
        } else {
            this.copyToClipboard(url, tooltip);
        }
    }

    copyToClipboard(text, tooltip) {
        navigator.clipboard.writeText(text).then(() => {
            if (tooltip) {
                tooltip.classList.add('is-visible');
                setTimeout(() => {
                    tooltip.classList.remove('is-visible');
                }, 2000);
            }
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    }
}

// Global instances
window.wishlistManager = new WishlistManager();
window.shareManager = new ShareManager();


class LocalizationForm extends HTMLElement {
    constructor() {
        super();
        this.elements = {
            input: this.querySelector('input[name="locale_code"]'),
            trigger: this.querySelector('.lang-dropdown__trigger'),
            panel: this.querySelector('.lang-dropdown__menu'),
        };

        // Toggle dropdown on trigger click
        if (this.elements.trigger && this.elements.panel) {
            this.elements.trigger.addEventListener('click', (e) => {
                e.preventDefault();
                const isOpen = this.elements.panel.hidden === false;
                this.elements.panel.hidden = isOpen;
                this.elements.trigger.setAttribute('aria-expanded', !isOpen);
            });
        }

        // Handle language selection
        this.querySelectorAll('.lang-dropdown__link').forEach(item => {
            item.addEventListener('click', this.onItemClick.bind(this));
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!this.contains(e.target) && this.elements.panel) {
                this.elements.panel.hidden = true;
                this.elements.trigger?.setAttribute('aria-expanded', 'false');
            }
        });
    }

    onItemClick(event) {
        event.preventDefault();
        const value = event.currentTarget.dataset.value || event.currentTarget.value;
        if (this.elements.input && value) {
            this.elements.input.value = value;
            this.querySelector('form')?.submit();
        }
    }
}

customElements.define('localization-form', LocalizationForm);




